// Overage decision rules (Planning/OverageDecision.md). Pure functions over the
// slice-2 ActualsSummary: how much approved actual labor cost exceeds the
// gig's overall max budget, what that is worth to the client, and where a
// recorded decision stands against the current numbers.
import type { ActualsSummary } from './actuals.js';
import type { OverageBreakdownRow, OverageDecision, QuoteLineItem } from './types.js';

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export interface Overage {
  // Σ Position.maxBudget — the gig's overall max budget.
  baselineCost: number;
  // Σ approved actual labor cost (approved entries only).
  actualCost: number;
  // Netted across positions: savings on one offset overruns on another.
  overageCost: number;
  marginPct: number;
  // overageCost × (1 + margin%) — what billing the client would add.
  billAmount: number;
  // Per position, informational (positive = over, negative = saved). Does not
  // change overageCost; it's context for the manager's decision.
  breakdown: OverageBreakdownRow[];
}

export function computeOverage(actuals: ActualsSummary, marginPct = 0): Overage {
  const breakdown = actuals.positions.map(
    (p): OverageBreakdownRow => ({
      positionId: p.positionId,
      role: p.role,
      label: p.label,
      budget: p.budgeted,
      actual: p.approvedCost,
      difference: round2(p.approvedCost - p.budgeted),
    }),
  );
  const baselineCost = round2(breakdown.reduce((t, r) => t + r.budget, 0));
  const actualCost = round2(breakdown.reduce((t, r) => t + r.actual, 0));
  const overageCost = round2(Math.max(0, actualCost - baselineCost));
  return {
    baselineCost,
    actualCost,
    overageCost,
    marginPct,
    billAmount: round2(overageCost * (1 + marginPct / 100)),
    breakdown,
  };
}

// The decision currently in force: the one no other decision supersedes
// (falling back to the latest if the chain is somehow broken).
export function currentDecision(decisions: OverageDecision[]): OverageDecision | undefined {
  if (decisions.length === 0) return undefined;
  const superseded = new Set(decisions.map(d => d.supersedes).filter((id): id is string => !!id));
  const live = decisions.filter(d => !superseded.has(d.id));
  const pool = live.length > 0 ? live : decisions;
  return [...pool].sort((a, b) => b.decidedAt.getTime() - a.decidedAt.getTime())[0];
}

export type OverageStatus =
  | { status: 'NONE' } // nothing over budget, nothing decided
  | { status: 'UNDECIDED' } // over budget, no decision yet
  | { status: 'DECIDED'; decision: OverageDecision }
  // A decision exists but the numbers it was based on have since changed
  // (entries edited/reopened, budget changed) — including falling back to $0.
  | { status: 'STALE'; decision: OverageDecision };

export function overageStatus(overage: Pick<Overage, 'overageCost'>, decisions: OverageDecision[]): OverageStatus {
  const decision = currentDecision(decisions);
  if (!decision) return overage.overageCost > 0 ? { status: 'UNDECIDED' } : { status: 'NONE' };
  return decision.overageCost === overage.overageCost ? { status: 'DECIDED', decision } : { status: 'STALE', decision };
}

export interface OverageDecisionInput {
  decision: 'BILL' | 'ABSORB';
  billAmount?: number;
  notes?: string;
}

// Returns an error message, or null when valid. ABSORB needs a reason (the
// audit trail must say why); BILL needs a positive amount.
export function validateOverageDecision(input: OverageDecisionInput): string | null {
  if (input.decision === 'ABSORB') {
    return input.notes?.trim() ? null : 'A reason is required to absorb an overage.';
  }
  const amount = input.billAmount;
  if (amount === undefined || !Number.isFinite(amount) || round2(amount) <= 0) {
    return 'Enter an amount greater than $0 to bill.';
  }
  return null;
}

// The staged line for a BILL decision: one manual line, tagged so a
// re-decision can find and replace it. The wording lists the positions that
// actually ran over, since the client's quote was per position.
export function buildOverageLine(
  lineId: string,
  decisionId: string,
  billAmount: number,
  breakdown: OverageBreakdownRow[],
): QuoteLineItem {
  const over = breakdown.filter(r => r.difference > 0);
  const names = over.map(r => (r.label ? `${r.role} (${r.label})` : r.role));
  const amount = round2(billAmount);
  return {
    id: lineId,
    source: 'manual',
    description: `Labor overage — actual labor exceeded the quoted budget${names.length ? ` (${names.join(', ')})` : ''}`,
    quantity: 1,
    unitCost: amount,
    lineTotal: amount,
    overageDecisionId: decisionId,
  };
}

// ---- Staging a BILL decision on a quote ------------------------------------
//
// A labor overage is billed through the existing change-order flow: an
// APPROVED quote can't be edited in place, so the line goes on a (new, or
// already-open DRAFT) change order. This is the pure planning half —
// recordOverageDecision (packages/client) applies the result in a transaction.

export type OverageBillingPlan =
  | {
      ok: true;
      // NEW_CHANGE_ORDER: the active quote is APPROVED → clone it (lines
      // locked) into a new DRAFT change order carrying the overage line.
      // UPDATE_DRAFT_CHANGE_ORDER: the active quote is already an open DRAFT
      // change order → just swap its staged overage line.
      mode: 'NEW_CHANGE_ORDER' | 'UPDATE_DRAFT_CHANGE_ORDER';
      lineItems: QuoteLineItem[];
      line: QuoteLineItem;
      // Overage already billed on lines carried from approved quotes, which
      // the new line tops up rather than duplicates.
      alreadyBilled: number;
    }
  | { ok: false; reason: string };

type QuoteLike = {
  state: 'DRAFT' | 'SENT' | 'APPROVED' | 'DECLINED';
  parentQuoteId?: string;
  lineItems: QuoteLineItem[];
};

const isOverageLine = (l: QuoteLineItem) => !!l.overageDecisionId;

export function planOverageBilling(
  quote: QuoteLike | null,
  lineId: string,
  decisionId: string,
  billAmount: number,
  breakdown: OverageBreakdownRow[],
): OverageBillingPlan {
  if (!quote) return { ok: false, reason: 'There is no quote for this gig yet — create and approve a quote before billing an overage.' };
  if (quote.state === 'SENT') {
    return { ok: false, reason: 'The current quote is out for client approval — resolve it (approve or decline) before billing an overage.' };
  }
  if (quote.state === 'DECLINED') {
    return { ok: false, reason: 'The current quote was declined — there is nothing approved to bill an overage against.' };
  }

  let base: QuoteLineItem[];
  let mode: 'NEW_CHANGE_ORDER' | 'UPDATE_DRAFT_CHANGE_ORDER';
  if (quote.state === 'APPROVED') {
    base = quote.lineItems.map(l => ({ ...l, locked: true }));
    mode = 'NEW_CHANGE_ORDER';
  } else if (quote.parentQuoteId) {
    // Open DRAFT change order: drop an earlier staged (unlocked) overage line
    // — this decision replaces it. Locked ones came from an approved parent.
    base = quote.lineItems.filter(l => !(isOverageLine(l) && !l.locked));
    mode = 'UPDATE_DRAFT_CHANGE_ORDER';
  } else {
    return { ok: false, reason: 'Approve the quote first — an overage is billed as a change order against an approved quote.' };
  }

  const alreadyBilled = round2(base.filter(isOverageLine).reduce((t, l) => t + l.lineTotal, 0));
  const delta = round2(billAmount - alreadyBilled);
  if (delta <= 0) {
    return {
      ok: false,
      reason: `$${alreadyBilled.toFixed(2)} of labor overage has already been billed on an approved quote, which covers this amount.`,
    };
  }
  const line = buildOverageLine(lineId, decisionId, delta, breakdown);
  if (alreadyBilled > 0) line.description += ' — additional';
  return { ok: true, mode, lineItems: [...base, line], line, alreadyBilled };
}

// For ABSORB (or a re-decision that no longer bills): the draft change
// order's staged overage line, if any, comes off. Returns null when there's
// nothing to remove. Lines already on approved quotes are never touched.
export function planOverageRemoval(quote: QuoteLike | null): QuoteLineItem[] | null {
  if (!quote || quote.state !== 'DRAFT' || !quote.parentQuoteId) return null;
  const kept = quote.lineItems.filter(l => !(isOverageLine(l) && !l.locked));
  return kept.length === quote.lineItems.length ? null : kept;
}
